import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  CreditCardOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BuildingOfficeIcon, BriefcaseIcon } from "@heroicons/react/24/outline";
import { EditFormContext } from "./PublicEmployeeEditForm";
import { useNavigate } from "react-router-dom";
import { salutations } from "../../Constants";

const EmployeeDetails = () => {
  const { formData, setFormData, loading } = useContext(EditFormContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArabicChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      arabicContent: {
        ...prevData.arabicContent,
        [name]: value,
      },
    }));
  };

  return (
    <div>
      <div className="grid md:grid-cols-2 grid-cols-1 gap-4 pb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("form.title")}
          </label>
          <select
            name="salutation"
            value={formData.salutation}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          >
            <option value="">{t("form.select")}</option>
            {salutations.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("form.arabicSupport")}
          </label>
          <div className="flex items-center mt-2">
            <div className="relative inline-block w-12 mr-2 align-middle">
              <input
                type="checkbox"
                name="arabicSupport"
                id="arabicSupport"
                checked={formData.arabicSupport}
                onChange={handleChange}
                className="absolute opacity-0 w-full h-full"
              />
              <div
                className={`block w-10 h-6 rounded-full transition-colors ${
                  formData.arabicSupport ? "bg-blue-500" : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                    formData.arabicSupport ? "transform translate-x-4" : ""
                  }`}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <UserOutlined size={16} className="mr-1" />
              {t("form.firstName")}
            </div>
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <UserOutlined size={16} className="mr-1" />
              {t("form.lastName")}
            </div>
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <MailOutlined size={16} className="mr-1" />
              {t("form.email")}
            </div>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <PhoneOutlined size={16} className="mr-1" />
              {t("form.phone")}
            </div>
          </label>
          <input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <BriefcaseIcon height={18} className="mr-1" />
              {t("form.role")}
            </div>
          </label>
          <input
            type="text"
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <CreditCardOutlined size={16} className="mr-1" />
              {t("form.employeeId")}
            </div>
          </label>
          <input
            type="text"
            name="employeeId"
            value={formData.employeeId}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center">
              <BuildingOfficeIcon height={18} className="mr-1" />
              {t("form.company")}
            </div>
          </label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        {formData.arabicSupport && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <UserOutlined size={16} className="mr-1" />
                  {t("form.firstNameArabic")}
                </div>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.arabicContent?.firstName || ""}
                onChange={handleArabicChange}
                className="w-full p-2 border border-gray-300 text-right rounded-md"
                required={formData.arabicSupport}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <UserOutlined size={16} className="mr-1" />
                  {t("form.lastNameArabic")}
                </div>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.arabicContent?.lastName || ""}
                onChange={handleArabicChange}
                className="w-full p-2 border border-gray-300 text-right rounded-md"
                required={formData.arabicSupport}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <BriefcaseIcon height={18} className="mr-1" />
                  Designation (Arabic)
                </div>
              </label>
              <input
                type="text"
                name="role"
                value={formData.arabicContent?.role}
                onChange={handleArabicChange}
                className="w-full p-2 border border-gray-300 text-right rounded-md"
                required={formData.arabicSupport}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <BuildingOfficeIcon height={18} className="mr-1" />
                  Company (Arabic)
                </div>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.arabicContent?.companyName}
                onChange={handleArabicChange}
                className="w-full p-2 border border-gray-300 text-right rounded-md"
                required={formData.arabicSupport}
              />
            </div>
          </>
        )}
      </div>
      <div className="flex gap-4">
        <button
          type="submit"
          className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
          disabled={loading}
        >
          {loading ? (
            <span className="animate-pulse">{t("buttons.saving")}</span>
          ) : (
            <span className="flex gap-2 justify-center">
              <SaveOutlined />
              {t("buttons.update")}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-md"
          disabled={loading}
        >
          {t("buttons.cancel")}
        </button>
      </div>
    </div>
  );
};

export default EmployeeDetails;
